import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, LayoutDashboard, MapPin, Clock, ChevronLeft, ChevronRight, Hotel, Navigation } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";

const mapsUrl = (h) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([h.address, h.postcode].filter(Boolean).join(", "))}`;

const ShiftRow = ({ e, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 px-4 py-3" data-testid={testid}>
    <div className="flex items-center gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-[#0F2547]">{e.event_name}</p>
        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtDate(e.work_date || e.event_date)}</span>
          {e.venue && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{e.venue}</span>}
          {e.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{e.time}</span>}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-[#0F2547]">{gbp(e.amount)}</p>
        <p className={`text-[10px] font-bold uppercase tracking-wider ${e.paid ? "text-[#007F3B]" : "text-[#ED8B00]"}`}
          data-testid={`${testid}-paid`}>
          {e.paid ? `Paid${e.paid_date ? ` ${fmtDate(e.paid_date)}` : ""}` : "Owed"}
        </p>
      </div>
    </div>
    {(e.hotels || []).map((h, i) => (
      <div key={i} className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid={`${testid}-hotel-${i}`}>
        <Hotel className="h-4 w-4 text-[#005EB8] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-[#0F2547]">{h.name}</p>
          <p className="text-[11px] text-slate-500">{[h.address, h.postcode].filter(Boolean).join(", ")}</p>
          {h.booking_ref && <p className="text-[11px] text-slate-600 mt-0.5">Booking ref: <span className="font-bold" data-testid={`${testid}-hotel-ref-${i}`}>{h.booking_ref}</span></p>}
        </div>
        <a href={mapsUrl(h)} target="_blank" rel="noreferrer" data-testid={`${testid}-hotel-directions-${i}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-[11px] font-bold transition-colors shrink-0">
          <Navigation className="h-3.5 w-3.5" /> Directions
        </a>
      </div>
    ))}
  </div>
);

export default function MyShifts() {
  const [data, setData] = useState(null);
  const [avail, setAvail] = useState({});
  const [availCursor, setAvailCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });

  useEffect(() => {
    api.get("/my/shifts").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/my/availability").then((r) => setAvail(r.data.availability || {})).catch((e) => console.debug("Availability load failed:", e?.message || e));
  }, []);

  const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const toggleAvail = async (dateStr) => {
    const cur = avail[dateStr] || "";
    const next = cur === "" ? "available" : cur === "available" ? "unavailable" : "";
    setAvail((a) => { const c = { ...a }; if (next) c[dateStr] = next; else delete c[dateStr]; return c; });
    try {
      await api.put("/my/availability", { date: dateStr, status: next });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const monthDays = (() => {
    const y = availCursor.getFullYear();
    const m = availCursor.getMonth();
    const count = new Date(y, m + 1, 0).getDate();
    return { offset: (new Date(y, m, 1).getDay() + 6) % 7, count, today: isoDay(new Date()), y, m };
  })();

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="my-shifts-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src={LOGO} alt="Wales Event Medical Services" className="h-9 w-auto" />
          <div>
            <p className="font-heading font-bold text-[#0F2547] text-sm leading-tight">My shifts & pay</p>
            <p className="text-[11px] text-slate-500">{data?.name || ""}</p>
          </div>
          <Link to="/dashboard" data-testid="my-shifts-back"
            className="ml-auto inline-flex items-center gap-2 text-xs font-semibold px-3 py-2.5 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]">
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        {!data ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl border border-red-100 p-4 text-center" data-testid="my-shifts-owed-card">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#D5281B]">Owed to you</p>
                <p className="font-extrabold text-2xl text-[#D5281B] mt-1" data-testid="my-shifts-owed">{gbp(data.amount_owed)}</p>
              </div>
              <div className="bg-white rounded-xl border border-emerald-100 p-4 text-center" data-testid="my-shifts-paid-card">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#007F3B]">Paid to you</p>
                <p className="font-extrabold text-2xl text-[#007F3B] mt-1" data-testid="my-shifts-paid">{gbp(data.amount_paid)}</p>
              </div>
            </div>

            <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="my-availability">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8]">My availability diary</h2>
                <div className="flex items-center gap-1">
                  <button onClick={() => setAvailCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))} data-testid="avail-prev"
                    className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm font-bold text-[#0F2547] w-28 text-center">{availCursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
                  <button onClick={() => setAvailCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))} data-testid="avail-next"
                    className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">Tap a day to mark when you can work (green) or can't (red) — tap again to change or clear. WEMS sees this when sending shifts out.</p>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d}>{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: monthDays.offset }).map((_, i) => <span key={`pad-${i}`} />)}
                {Array.from({ length: monthDays.count }, (_, i) => {
                  const dateStr = isoDay(new Date(monthDays.y, monthDays.m, i + 1));
                  const st = avail[dateStr] || "";
                  const past = dateStr < monthDays.today;
                  return (
                    <button key={dateStr} disabled={past} onClick={() => toggleAvail(dateStr)}
                      data-testid={`avail-day-${dateStr}`}
                      className={`h-10 rounded-lg text-xs font-bold transition-colors ${st === "available" ? "bg-[#007F3B] text-white" : st === "unavailable" ? "bg-[#D5281B] text-white" : "bg-slate-50 text-slate-600 hover:bg-sky-50"} ${past ? "opacity-30" : ""}`}>
                      {i + 1}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-3 text-[10px] font-bold text-slate-500">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#007F3B]" /> I can work</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-[#D5281B]" /> I can't work</span>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-2">Upcoming shifts</h2>
              {data.upcoming.length === 0 ? (
                <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-4" data-testid="my-shifts-no-upcoming">
                  Nothing booked yet — when you're put on an event it'll show here.
                </p>
              ) : (
                <div className="space-y-2">
                  {data.upcoming.map((e) => <ShiftRow key={`${e.event_id}-${e.entry_id}`} e={e} testid={`my-shift-upcoming-${e.entry_id}`} />)}
                </div>
              )}
            </section>

            <section>
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Past shifts</h2>
              {data.past.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="my-shifts-no-past">No past shifts yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.past.map((e) => <ShiftRow key={`${e.event_id}-${e.entry_id}`} e={e} testid={`my-shift-past-${e.entry_id}`} />)}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
